import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button } from 'reactstrap';
import classnames from 'classnames';
import Spinner from 'react-spinkit';
import { buttons as Buttons } from '@breezeway/brzw-components';
import { useNewStyleGuide } from '@internalStore/style_guide/selectors';
import { getActiveDimensions } from '@shared/active_resize_listener/selectors';
import { DIMENSION_STATES } from '../../utils/dimension';

const { SMALL, MEDIUM } = Buttons.constants.BUTTON_SIZES;

const StyleguideFormButton = ({
  className,
  disabled,
  disabledPrimary,
  disabledWithIndicator,
  inModal,
  isMobile,
  onPrimaryClick,
  onPrimaryUrl,
  onSecondaryClick,
  onSecondaryUrl,
  paddingClassName,
  primaryAutoWidth,
  primaryButtonClassName,
  primaryUrlNewTab,
  primaryText,
  secondaryAutoWidth,
  secondaryText,
}) => {
  const isPrimaryDisabled = disabled || disabledPrimary;
  let buttonSize = null;

  if (inModal) {
    if (isMobile) {
      buttonSize = SMALL;
    } else {
      buttonSize = MEDIUM;
    }
  } else {
    buttonSize = isMobile
      ? Buttons.constants.BUTTON_SIZES.LARGE
      : Buttons.constants.BUTTON_SIZES.MEDIUM;
  }

  const primaryProps = { disabled: isPrimaryDisabled, size: buttonSize };
  const secondaryProps = { size: buttonSize, disabled };

  if (onPrimaryClick) {
    primaryProps.onClick = onPrimaryClick;
  } else if (onPrimaryUrl) {
    if (primaryUrlNewTab) {
      primaryProps.target = '_blank';
    }
    primaryProps.tag = Link;
    primaryProps.to = onPrimaryUrl;
  }

  if (onSecondaryClick) {
    secondaryProps.onClick = onSecondaryClick;
  } else if (onSecondaryUrl) {
    secondaryProps.size = Buttons.constants.BUTTON_SIZES.SMALL;
    secondaryProps.tag = Link;
    secondaryProps.to = onSecondaryUrl;
  }

  return (
    <div
      className={classnames('form__btn-container', paddingClassName, className, {
        'form__btn-container--single': !(onSecondaryClick || onSecondaryUrl),
      })}
    >
      <Buttons.TextButton
        autoWidth={primaryAutoWidth}
        className={classnames({
          'form__btn--margin-right':
            !primaryButtonClassName && (onSecondaryClick || onSecondaryUrl),
        })}
        isFetching={isPrimaryDisabled && disabledWithIndicator}
        type={onPrimaryClick ? 'button' : 'submit'}
        {...primaryProps}
      >
        {primaryText}
      </Buttons.TextButton>

      {(onSecondaryClick || onSecondaryUrl) && (
        <Buttons.TextButton
          autoWidth={secondaryAutoWidth}
          disabled={isPrimaryDisabled}
          theme={Buttons.constants.BUTTON_THEMES.SECONDARY}
          {...secondaryProps}
        >
          {secondaryText}
        </Buttons.TextButton>
      )}
    </div>
  );
};

StyleguideFormButton.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  disabledPrimary: PropTypes.bool,
  disabledWithIndicator: PropTypes.bool,
  inModal: PropTypes.bool,
  isMobile: PropTypes.bool.isRequired,
  primaryUrlNewTab: PropTypes.bool,
  onPrimaryClick: PropTypes.func,
  onPrimaryUrl: PropTypes.string,
  onSecondaryClick: PropTypes.func,
  onSecondaryUrl: PropTypes.string,
  paddingClassName: PropTypes.string,
  primaryButtonClassName: PropTypes.string,
  primaryAutoWidth: PropTypes.bool,
  primaryText: PropTypes.string,
  secondaryAutoWidth: PropTypes.bool,
  secondaryText: PropTypes.string,
};

StyleguideFormButton.defaultProps = {
  disabled: false,
  disabledPrimary: false,
  disabledWithIndicator: false,
  inModal: false,
  primaryUrlNewTab: false,
  onSecondaryClick: null,
  paddingClassName: 'pt-3 pb-4 pb-lg-0',
  primaryButtonClassName: null,
  primaryAutoWidth: false,
  primaryText: 'Submit',
  secondaryAutoWidth: false,
  secondaryText: 'Cancel',
};

const FormButtons = props => {
  const primaryProps = {};
  const secondaryProps = {};

  primaryProps.disabled = props.disabled || props.disabledPrimary;
  secondaryProps.disabled = props.disabled;

  if (props.onPrimaryClick) {
    primaryProps.onClick = props.onPrimaryClick;
  } else if (props.onPrimaryUrl) {
    if (props.primaryUrlNewTab) {
      primaryProps.target = '_blank';
    }
    primaryProps.tag = Link;
    primaryProps.to = props.onPrimaryUrl;
  }

  if (props.onSecondaryClick) {
    secondaryProps.onClick = props.onSecondaryClick;
  } else if (props.onSecondaryUrl) {
    secondaryProps.tag = Link;
    secondaryProps.to = props.onSecondaryUrl;
  }

  return props.useNewStyleGuide ? (
    <StyleguideFormButton {...props} />
  ) : (
    <div className={classnames('form__btn-container', props.paddingClassName, props.className)}>
      <Button
        color={props.primaryColor}
        className={classnames('form__btn form__submit', {
          'form__submit-wide': props.primaryWide,
          'form__btn-with-indicator':
            (props.disabled || props.disabledPrimary) && props.disabledWithIndicator,
        })}
        {...primaryProps}
      >
        {(props.disabled || props.disabledPrimary) && props.disabledWithIndicator && (
          <Spinner name="circle" color="white" className="form__submit-indicator" fadeIn="none" />
        )}
        <span className={classnames(`icon-${props.primaryIcon}`)}>{props.primaryText}</span>
      </Button>

      {props.onSecondaryClick || props.onSecondaryUrl ? (
        <div className="form__cancel">
          <Button
            className="form__cancel form__btn align-self-start"
            color={props.secondaryColor}
            {...secondaryProps}
          >
            <span className={classnames(`icon-${props.secondaryIcon}`)}>{props.secondaryText}</span>
          </Button>
        </div>
      ) : null}
    </div>
  );
};

FormButtons.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  disabledPrimary: PropTypes.bool,
  disabledWithIndicator: PropTypes.bool,
  inModal: PropTypes.bool,
  primaryUrlNewTab: PropTypes.bool,
  onPrimaryClick: PropTypes.func,
  onPrimaryUrl: PropTypes.string,
  onSecondaryClick: PropTypes.func,
  onSecondaryUrl: PropTypes.string,
  paddingClassName: PropTypes.string,
  primaryColor: PropTypes.string,
  primaryIcon: PropTypes.string,
  primaryText: PropTypes.string,
  primaryWide: PropTypes.bool,
  secondaryColor: PropTypes.string,
  secondaryIcon: PropTypes.string,
  secondaryText: PropTypes.string,
  useNewStyleGuide: PropTypes.bool,
  withStyleguide: PropTypes.bool,
};

FormButtons.defaultProps = {
  disabled: false,
  disabledPrimary: false,
  disabledWithIndicator: false,
  inModal: false,
  primaryUrlNewTab: false,
  onSecondaryClick: null,
  paddingClassName: 'pt-3 pb-4 pb-lg-0',
  primaryColor: 'primary',
  primaryIcon: '',
  primaryText: 'Submit',
  primaryWide: false,
  secondaryColor: 'outline-secondary',
  secondaryIcon: '',
  secondaryText: 'Cancel',
  useNewStyleGuide: false,
};

const mapStateToProps = (state, props) => ({
  useNewStyleGuide: useNewStyleGuide(state, props.withStyleguide),
  isMobile: getActiveDimensions(state).state === DIMENSION_STATES.MOBILE,
});

export default connect(
  mapStateToProps,
  null,
)(FormButtons);
